import QRCode from 'qrcode';
import { jsPDF } from 'jspdf';

// ── TRACE QR Labels ──────────────────────────────────────────────
// QR codes encode kiosk URLs, so any phone camera scan opens the
// kiosk pre-focused on the job (or filtered to the location):
//   https://app.traceoperations.com/kiosk/<slug>?job=<job_number>
//   https://app.traceoperations.com/kiosk/<slug>?location=<id>

const APP_BASE = 'https://app.traceoperations.com';

const jobUrl = (slug, jobNumber) =>
  `${APP_BASE}/kiosk/${slug}?job=${encodeURIComponent(jobNumber)}`;

const locationUrl = (slug, locationId) =>
  `${APP_BASE}/kiosk/${slug}?location=${encodeURIComponent(locationId)}`;

const qrDataUrl = (text) =>
  QRCode.toDataURL(text, { width: 600, margin: 1, errorCorrectionLevel: 'M' });

// 4 labels per US-letter page: 2 cols x 2 rows.
const PAGE_W = 215.9; // mm
const PAGE_H = 279.4;
const COLS = 2;
const ROWS = 2;
const CELL_W = PAGE_W / COLS;
const CELL_H = PAGE_H / ROWS;

async function renderLabelSheet(doc, items) {
  for (let i = 0; i < items.length; i++) {
    const { qrText, title, subtitle, footer } = items[i];

    const pos = i % (COLS * ROWS);
    if (i > 0 && pos === 0) doc.addPage();

    const col = pos % COLS;
    const row = Math.floor(pos / COLS);
    const x0 = col * CELL_W;
    const y0 = row * CELL_H;
    const cx = x0 + CELL_W / 2;

    // Cell border (cut guide)
    doc.setDrawColor(200);
    doc.setLineDashPattern([2, 2], 0);
    doc.rect(x0 + 4, y0 + 4, CELL_W - 8, CELL_H - 8);
    doc.setLineDashPattern([], 0);

    // QR
    const qr = await qrDataUrl(qrText);
    const qrSize = 62;
    doc.addImage(qr, 'PNG', cx - qrSize / 2, y0 + 14, qrSize, qrSize);

    // Title (job number / location name) -- big and readable from distance
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(26);
    doc.setTextColor(20);
    doc.text(String(title), cx, y0 + 88, { align: 'center' });

    // Subtitle (description), truncated
    if (subtitle) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(90);
      const sub = String(subtitle).length > 60 ? String(subtitle).slice(0, 57) + '…' : String(subtitle);
      doc.text(sub, cx, y0 + 98, { align: 'center', maxWidth: CELL_W - 24 });
    }

    // Footer
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(150);
    doc.text(footer || 'TRACE — scan with phone camera', cx, y0 + CELL_H - 12, { align: 'center' });
  }
}

/**
 * Print QR labels for one or more jobs.
 * jobs: [{ job_number, description }], slug: org slug
 */
export async function printJobLabels(jobs, slug) {
  const doc = new jsPDF({ unit: 'mm', format: 'letter' });
  await renderLabelSheet(
    doc,
    jobs.map((j) => ({
      qrText: jobUrl(slug, j.job_number),
      title: `#${j.job_number}`,
      subtitle: j.description,
      footer: 'TRACE job label — scan to load this job',
    }))
  );
  doc.save(jobs.length === 1 ? `trace-job-${jobs[0].job_number}.pdf` : 'trace-job-labels.pdf');
}

/**
 * Print QR tags for one or more locations.
 * locations: [{ id, name }], slug: org slug
 */
export async function printLocationLabels(locations, slug) {
  const doc = new jsPDF({ unit: 'mm', format: 'letter' });
  await renderLabelSheet(
    doc,
    locations.map((l) => ({
      qrText: locationUrl(slug, l.id),
      title: l.name,
      subtitle: null,
      footer: 'TRACE location tag — scan to view this location',
    }))
  );
  doc.save(locations.length === 1 ? `trace-location-${locations[0].name}.pdf` : 'trace-location-tags.pdf');
}